import {
  getFirestore,
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc,
  documentId,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import firebaseApp from "@/lib/firebase";
import type { Course } from "@/types/course";
import { listRepository } from "./ListRepository";

// Helper function to make authenticated Firebase function calls

/**
 * Repository class for Course collection operations
 * Handles CRUD operations for courses in Firestore
 */
export class CourseRepository {
  private db = getFirestore(firebaseApp);
  private collectionName = "courses" as const;

  // ========== CRUD Operations ==========

  /**
   * Create a new course
   */
  async create(
    courseData: Omit<Course, "id">,
    schoolId: string,
    modulesIds: string[]
  ) {
    try {
      const docRef = await addDoc(
        collection(this.db, this.collectionName),
        courseData
      );
      const courseId = docRef.id;
      await listRepository.upsertSchoolCoursesList(schoolId, [courseId]);
      await listRepository.upsertCourseModulesList({ courseId, modulesIds });
      const newCourseData = {
        id: docRef.id,
        ...courseData,
      } as Course;
      return newCourseData as Course;
    } catch (error) {
      console.error("Error creating course:", error);
      throw error;
    }
  }

  /**
   * Clone a course
   */
  async clone(originalCourseId: string, schoolId: string): Promise<Course> {
    try {
      const docRef = await this.getById(originalCourseId);
      if (!docRef) {
        throw new Error(`Course with ID ${originalCourseId} not found`);
      }
      const { id, ...data } = docRef;

      const clonedCourse: Omit<Course, "id"> = {
        ...data,
        title: `Copy of ${docRef.title}`,
        createdAt: Timestamp.now(),
        lastModified: Timestamp.now(),
      };

      const courseModules = await listRepository.getCourseModules(
        originalCourseId
      );
      const modulesIds = courseModules?.modulesIds || [];

      const tempClonedCourse = await this.create(
        clonedCourse,
        schoolId,
        modulesIds
      );

      return tempClonedCourse as Course;
    } catch (error) {
      console.error("Error cloning course:", error);
      throw error;
    }
  }

  /**
   * Update an existing course
   */
  async update(courseData: Course, modulesIds: string[]): Promise<void> {
    try {
      const docRef = await this.getById(courseData.id);
      if (!docRef) {
        throw new Error(`Course with ID ${courseData.id} not found`);
      }

      const updatedModule: Course = {
        ...docRef,
        ...courseData,
        lastModified: Timestamp.now(),
      } as Course;
      await updateDoc(doc(this.db, this.collectionName, courseData.id), {
        ...updatedModule,
      });

      await listRepository.upsertCourseModulesList({
        courseId: courseData.id,
        modulesIds,
      });
    } catch (error) {
      console.error("Error updating course:", error);
      throw error;
    }
  }

  /**
   * Delete a course
   */
  async delete(id: string): Promise<void> {
    try {
      await deleteDoc(doc(this.db, this.collectionName, id));
    } catch (error) {
      console.error("Error deleting course:", error);
      throw error;
    }
  }

  /**
   * Get a single course by ID
   */
  async getById(id: string): Promise<Course | null> {
    const docRef = doc(this.db, this.collectionName, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as Course;
  }

  /**
   * Check if a course exists
   */
  async exists(id: string): Promise<boolean> {
    const docRef = doc(this.db, this.collectionName, id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  }

  // ========== Query Operations ==========
  /**
   * Get all courses by ids
   */
  async getByIds(ids: string[]): Promise<Course[]> {
    if (ids.length === 0) return [];
    const q = query(
      collection(this.db, this.collectionName),
      where(documentId(), "in", ids)
    );
    const snapshot = await getDocs(q);
    const courses = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const numberOfModules = (
          await listRepository.getModuleIdsForCourse(doc.id)
        ).length;

        return {
          id: doc.id,
          ...doc.data(),
          numberOfModules,
        } as Course;
      })
    );
    return courses;
  }

  /**
   * Get all courses created by a specific user
   */
  async getByCreatorId(creatorId: string): Promise<Course[]> {
    const q = query(
      collection(this.db, this.collectionName),
      where("creatorId", "==", creatorId)
    );
    const snapshot = await getDocs(q);

    const courses = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const numberOfModules = (
          await listRepository.getModuleIdsForCourse(doc.id)
        ).length;

        return {
          id: doc.id,
          ...doc.data(),
          numberOfModules,
        } as Course;
      })
    );

    return courses;
  }

  /**
   * Get all public courses
   */
  async getPublicCourses(): Promise<Course[]> {
    const q = query(
      collection(this.db, this.collectionName),
      where("public", "==", true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Course[];
  }

  // ========== Utility Methods ==========

  // ========== Clone Operations ==========
}

// Export singleton instance
export const courseRepository = new CourseRepository();
